import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { s, authHeaders, ExcelIcon, exportToExcel } from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import { C360Table, ColumnChooser, ViewIcon } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';

const API_BASE = '/api/visitsessions';
const PAGE_SIZE_DEFAULT = 20;

const STATUS_TABS = [
  { key: '', label: 'Tümü' },
  { key: 'OPEN', label: 'Açık' },
  { key: 'CLOSED', label: 'Kapalı' },
];

const COLUMN_FILTER_META = {
  representativeName: { type: 'text' },
  status: { type: 'select' },
  dayDate: { type: 'text' },
};

function pick(obj, ...keys) {
  for (const k of keys) if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    openCount: pick(data, 'openCount', 'OpenCount') ?? 0,
    closedCount: pick(data, 'closedCount', 'ClosedCount') ?? 0,
    totalVisits: pick(data, 'totalVisits', 'TotalVisits') ?? 0,
  };
}

function toIsoStart(d) { return d ? new Date(`${d}T00:00:00`).toISOString() : ''; }
function toIsoEnd(d) { return d ? new Date(`${d}T23:59:59`).toISOString() : ''; }

export function formatDateTime(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function formatMinutes(min) {
  if (min == null) return '-';
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h > 0 ? `${h}s ${m}dk` : `${m}dk`;
}

function statusBadge(status) {
  if (status === 'OPEN') return <StatusBadge tone="wait">Açık</StatusBadge>;
  if (status === 'CLOSED') return <StatusBadge tone="ok">Kapalı</StatusBadge>;
  return <StatusBadge tone="neutral">{status || '-'}</StatusBadge>;
}

export function FieldVisitsPage() {
  usePageMeta('Saha Ziyaretleri', 'Temsilcilerin gün oturumları ve o gün yaptıkları');
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ openCount: 0, closedCount: 0, totalVisits: 0 });
  // Yusuf Fix — LİSTE AÇILIŞINDA "Kayıt bulunamadı" YANIP SÖNMESİN: veri mount'taki effect'te
  // çekiliyor, yani ilk boyama fetch'ten ÖNCE olur. loading false başlayınca tablo o ilk karede
  // boş-durum metnini gösteriyor, veri gelince listeye dönüyordu — kullanıcı "önce kayıt yok dedi,
  // sonra liste geldi" diye okuyor. Yükleme durumu ilk kareden itibaren AÇIK başlar.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sorting, setSorting] = useState([{ id: 'startedAt', desc: true }]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnFilters, setColumnFilters] = useState({});
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const isoFrom = toIsoStart(dateFrom);
      const isoTo = toIsoEnd(dateTo);
      if (isoFrom) params.set('from', isoFrom);
      if (isoTo) params.set('to', isoTo);
      params.set('take', '500');

      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const data = unwrapResponse(await res.json());
      setRows(Array.isArray(data.items) ? data.items : []);
      setCounts({ openCount: data.openCount, closedCount: data.closedCount, totalVisits: data.totalVisits });
      setCurrentPage(1);
    } catch (e) {
      setError(e.message || 'Liste alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const openDetail = useCallback((row) => {
    navigate(`/reports/field-visits/${pick(row, 'id', 'Id')}`);
  }, [navigate]);

  // Serbest metin araması istemci tarafında — liste tek sayfada (take=500) geliyor.
  const filteredRows = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    if (!needle) return rows;
    return rows.filter((r) => {
      const hay = [r.representativeName, r.status, r.dayDate].filter(Boolean).join(' ').toLocaleLowerCase('tr-TR');
      return hay.includes(needle);
    });
  }, [rows, filterText]);

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('startedAt', {
      header: 'Başlangıç',
      cell: (info) => formatDateTime(info.getValue()),
    }),
    columnHelper.accessor('endedAt', {
      header: 'Bitiş',
      cell: (info) => (info.getValue() ? formatDateTime(info.getValue()) : <span style={{ color: 'var(--warn)', fontWeight: 700 }}>Devam ediyor</span>),
    }),
    columnHelper.accessor('representativeName', {
      header: 'Temsilci', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => statusBadge(info.getValue()),
    }),
    columnHelper.accessor('durationMinutes', {
      header: 'Oturum Süresi',
      cell: (info) => formatMinutes(info.getValue()),
    }),
    columnHelper.accessor('visitCount', { header: 'Ziyaret' }),
    columnHelper.accessor('customerCount', { header: 'Müşteri' }),
    columnHelper.accessor('completedCount', { header: 'Tamamlanan' }),
    columnHelper.accessor('cancelledCount', { header: 'İptal' }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button
            type="button"
            className="c360-action-btn c360-action-btn--icon c360-action-btn--view"
            title="Detay" aria-label="Detay"
            onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}
          >
            <ViewIcon />
          </button>
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [openDetail]);

  const totalCount = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: filteredRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater({ pageIndex: currentPage - 1, pageSize }) : updater;
      setCurrentPage(next.pageIndex + 1);
      setPageSize(next.pageSize);
    },
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(pick(row, 'id', 'Id')),
  });

  const pageRowCount = table.getRowModel().rows.length;

  const handleExport = () => {
    const cols = [
      { key: 'startedAt', label: 'Başlangıç' },
      { key: 'endedAt', label: 'Bitiş' },
      { key: 'representativeName', label: 'Temsilci' },
      { key: 'status', label: 'Durum' },
      { key: 'durationMinutes', label: 'Süre (dk)' },
      { key: 'visitCount', label: 'Ziyaret' },
      { key: 'customerCount', label: 'Müşteri' },
    ];
    const dateStr = new Date().toISOString().slice(0, 10);
    exportToExcel(filteredRows, cols, `saha_ziyaretleri_${dateStr}`);
  };

  const toolbarNode = (
    <>
      {STATUS_TABS.map((tab) => (
        <button
          key={tab.key || 'all'}
          type="button"
          onClick={() => setStatusFilter(tab.key)}
          style={{ ...(statusFilter === tab.key ? s.btnPrimary : s.btnSecondary), padding: '6px 14px', fontSize: 13 }}
        >
          {tab.label}
        </button>
      ))}
      <span style={{ width: 1, height: 22, background: '#e5e7eb', margin: '0 4px' }} />
      <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ ...s.filterInput, width: 140 }} title="Başlangıç tarihi" />
      <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ ...s.filterInput, width: 140 }} title="Bitiş tarihi" />
      <input
        style={s.filterInput}
        type="text"
        placeholder="Temsilci / tarih..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') setFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); setFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={load} disabled={loading}>{loading ? 'Yükleniyor...' : 'Yenile'}</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button
          type="button"
          style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}
        >
          <ExcelIcon size={16} />
        </button>
      </span>
    </>
  );

  const metaNode = (
    <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <StatusBadge tone="wait">Açık: {counts.openCount}</StatusBadge>
      <StatusBadge tone="ok">Kapalı: {counts.closedCount}</StatusBadge>
      <StatusBadge tone="neutral">Toplam Ziyaret: {counts.totalVisits}</StatusBadge>
      {error && <StatusBadge tone="bad">{error}</StatusBadge>}
    </span>
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Saha Ziyaretleri"
        subtitle="Her satır bir gün oturumudur: temsilci 'Güne Başla' dediğinde açılır, 'Günü Bitir' dediğinde kapanır. O aralıkta yapılan tüm ziyaretler bu oturuma bağlıdır; aynı temsilci aynı gün birden çok oturum açabilir."
        meta={metaNode}
        toolbar={toolbarNode}
        footer={(
          <Pagination
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={pageSize}
            rowCount={pageRowCount}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        )}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={(k, v) => setColumnFilters((p) => ({ ...p, [k]: v }))}
          onClearFilter={(k) => setColumnFilters((p) => { const n = { ...p }; delete n[k]; return n; })}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Oturum bulunamadı"
        />
      </ListPageShell>
    </div>
  );
}

export default FieldVisitsPage;
